import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import PropTypes from 'prop-types';

import { retryNonRoutableRequestRouting } from 'store/thunks/workQueueThunks';

import { NON_ROUTABLE_SOURCE } from '../constants/nonRoutableRequests';
import { useNonRoutableResolution } from '../hooks/useNonRoutableResolution';
import { nonRoutableRetryIsSafe } from '../utils/nonRoutableRequestErrors';
import NonRoutableRequestIdentity from './NonRoutableRequestIdentity';
import NonRoutableResolutionOutcome from './NonRoutableResolutionOutcome';

// AN API INTAKE RETRY IS ASYNCHRONOUS. The mutation returns the row RESOLUTION_IN_PROGRESS and the
// replay decides later, so this message is DELIBERATELY NOT "Routing succeeded": the request may
// find a destination or be parked again, and claiming success would be wrong half the time on a
// screen where the other reading is "an order now exists".
const ASYNC_RETRY =
  'Routing was re-run. Refresh the queue to see whether the request found a destination or was parked again.';

/**
 * POM-6279 P4 — AN ESP / POM RETRY IS SYNCHRONOUS: the replay closes or re-parks the row inside
 * the mutation, so the returned detail IS the outcome and the message can say which.
 *   RESOLVED (RETRY_ROUTED) — routed; the request is closed.
 *   PENDING                 — routing reached the same answer and parked it again. `info`, not a
 *                             success: nothing moved. Its lastError says why (a refused replay
 *                             reads RETRY:<code>, but a refusal arrives as a typed error instead).
 *   anything else           — the asynchronous intake path above.
 */
const successMessage = (detail) => {
  if (detail?.status === 'RESOLVED') {
    return 'Routed. The order found a destination and this request is closed.';
  }
  if (detail?.status === 'PENDING') {
    return {
      variant: 'info',
      message: `Routing ran again and the request is parked again${
        detail.lastError ? ` (${detail.lastError})` : ''
      }. It stays in this queue.`,
    };
  }
  return ASYNC_RETRY;
};

/**
 * RETRY ROUTING FOR A PARKED REQUEST (POM-6223).
 *
 * THE ONLY ONE OF THE THREE ACTIONS THAT IS NOT TERMINAL. Bring Back creates an order that will be
 * dispensed; Cancel guarantees the prescription is never filled. This re-runs the ENTIRE routing
 * workflow from the first step against the CURRENT configuration and lets the outcome decide —
 * which is why its confirm button is neutral rather than error-coloured, and why the copy refuses
 * to predict which outcome the operator is about to get.
 *
 * IT DOES NOT REPLAY THE OLD DECISION. The backend re-invokes the Routing Hub and fails closed if
 * the hub is unreachable or answers un-enriched, leaving the request parked rather than replaying
 * a stale decision and reporting success. That is the entire point: the operator clicks this
 * BECAUSE they changed the configuration, so an answer computed before that change is worthless.
 */
const RetryNonRoutableRequestRoutingDialog = ({ open, onClose, item }) => {
  const { submit, submitting, outcome, dismiss } = useNonRoutableResolution({
    item,
    action: retryNonRoutableRequestRouting,
    successMessage,
    onClose,
    // Resets the outcome each time the dialog is opened. The row menu keeps all three dialogs
    // mounted, so without this a previous attempt's alert would still be on screen with no
    // confirm button.
    open,
  });

  // Every dismissal goes through `dismiss`, which refreshes the queue on the way out — the refetch
  // is deliberately NOT done when the mutation returns, because this dialog lives inside the row
  // it is resolving and the refresh would unmount it mid-message.
  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    dismiss();
  };

  // Same rule as the other two: after an attempt, only a backend-confirmed "nothing happened, the
  // row is back where it started" earns another click. A retry that reached the publish has
  // already acted, and a stuck row is an escalation rather than something to click again.
  const canSubmit = !outcome || nonRoutableRetryIsSafe(outcome.described);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='RetryNonRoutableRequestRoutingDialog-title'
    >
      <DialogTitle id='RetryNonRoutableRequestRoutingDialog-title'>Retry Routing?</DialogTitle>
      <DialogContent>
        <NonRoutableRequestIdentity item={item} />

        <Alert severity='info'>
          <AlertTitle>Routing runs again from the beginning</AlertTitle>
          Every routing check is re-run against the <strong>current</strong> configuration. If a
          destination is now available the order continues into it and{' '}
          <strong>will be dispensed</strong>. If not, the request is parked again and its attempt
          count goes up.
          {item.source === NON_ROUTABLE_SOURCE.ESP_POM &&
            ' For an ESP / POM order the retry runs now, and its result is shown when it finishes.'}
        </Alert>

        {/* POM-6279 P4: a claim whose synchronous Retry died. The server offers Retry ONLY here —
            the dead replay may have written, and a Retry is what finds that out safely. */}
        {item.isStaleClaim && (
          <Alert severity='warning' sx={{ mt: 2 }} data-testid='non-routable-stale-retry'>
            <AlertTitle>The last retry of this order did not finish</AlertTitle>
            Retrying now takes it over and finds out what, if anything, it already created.
          </Alert>
        )}

        {outcome && <NonRoutableResolutionOutcome outcome={outcome} />}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={dismiss} disabled={submitting}>
          {outcome && !canSubmit ? 'Close' : 'Leave it parked'}
        </Button>
        {canSubmit && (
          <LoadingButton
            variant='contained'
            color='primary'
            loading={submitting}
            onClick={submit}
            id={`RetryNonRoutableRequestRoutingDialog-${item.nonRoutableRequestId}-confirm`}
          >
            Retry Routing
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

RetryNonRoutableRequestRoutingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    nonRoutableRequestId: PropTypes.string.isRequired,
    referenceId: PropTypes.string,
    status: PropTypes.string,
    source: PropTypes.string,
    isStaleClaim: PropTypes.bool,
  }).isRequired,
};

export default RetryNonRoutableRequestRoutingDialog;
